"use client"

import { useCallback, useState } from "react"
import { Pressable } from "react-native"
import { useQueryClient } from "@tanstack/react-query"
import clsx from "clsx"
import { FormattedMessage } from "react-intl"

import {
  Avatar,
  Box,
  isWeb,
  Loading,
  P,
  ScrollBox,
  Sheet,
  TextInput,
  useChatUserSearch,
  useCreateMessageChannel,
  useLoggingContext,
} from "@bullseye/components"
import { useRouter } from "@bullseye/navigation"
import type { Modal } from "@bullseye/types"

export const NewMessage = ({ isOpen, dismiss }: Modal) => {
  const { createMessageChannelAsync, status } = useCreateMessageChannel()
  const { captureException } = useLoggingContext()
  const [query, setQuery] = useState<string>("")
  const { users = [] } = useChatUserSearch(query, { excludeBlocked: true })
  const [excludedIds, setExcludedIds] = useState<Set<string>>(new Set())
  const [errorText, setErrorText] = useState<string>("")
  const router = useRouter()
  const queryClient = useQueryClient()

  const onClose = useCallback(() => {
    setQuery("")
    dismiss()
  }, [dismiss])

  return (
    <Sheet
      title={<FormattedMessage id="messenger.modal.newMessage.title" />}
      icon="x"
      show={isOpen}
      onIconPress={onClose}
      dismiss={onClose}
      contentContainerStyle="overflow-hidden"
      size="lg"
      renderContent={() => {
        return (
          <Box className="h-full w-full flex-col items-start g-6">
            <TextInput
              editable
              className="w-full"
              placeholder="Start typing for suggestions..."
              accessibilityLabel="Start typing for suggestions..."
              onChangeText={(text) => setQuery(text)}
              value={query}
            />
            <ScrollBox
              className={clsx("max-h-full w-full md:max-h-[80%]", {
                hidden: status === "loading",
              })}
              contentContainerStyle={{
                gap: 8,
              }}
            >
              {users
                .filter((u) => !excludedIds.has(u.id))
                .map((user) => {
                  const [commonExp] = user.relation.experiences || []

                  return (
                    <Pressable
                      key={user.id}
                      accessibilityRole="button"
                      accessibilityLabel={`Message ${user.formatted_name}`}
                      onPress={async () => {
                        setErrorText("")
                        try {
                          const response = await createMessageChannelAsync({
                            body: {
                              is_group_message: false,
                              users: [{ user_id: user.id }],
                            },
                          })

                          await queryClient.invalidateQueries(["allChannels"])

                          if (isWeb()) {
                            router.push(
                              `?channel=${response.data?.data[0]?.stream_channel_id}`,
                            )
                          } else {
                            router.push(
                              `messages/${response.data?.data[0]?.stream_channel_id}`,
                            )
                          }

                          onClose()
                        } catch (error) {
                          setExcludedIds((prev) => new Set([...prev, user.id]))
                          setErrorText(
                            "You can't message this member. They may have messaging disabled or have blocked you.",
                          )
                          captureException(
                            new Error(
                              `Failed to create message channel: ${error}`,
                            ),
                          )
                        }
                      }}
                    >
                      <Box className="flex-row items-center g-3">
                        <Avatar
                          imageUrl={user.avatar?.url}
                          name={user.formatted_name}
                          size="xl"
                        />
                        <Box className="flex g-1">
                          <P>{user.formatted_name}</P>
                          <P className="text-gray-600" size="sm">
                            {commonExp?.title}
                          </P>
                        </Box>
                      </Box>
                    </Pressable>
                  )
                })}
            </ScrollBox>
            {!!errorText && (
              <Box>
                <P className="text-red-600" size="sm">
                  {errorText}
                </P>
              </Box>
            )}
            <Box
              className={clsx("flex h-full w-full items-center", {
                hidden: status !== "loading",
              })}
            >
              <Loading />
            </Box>
          </Box>
        )
      }}
    />
  )
}
